import React, { useState, useEffect } from "react";

import ReunionDashboardHero from "./modules/reunion/ReunionDashboardHero";
import ReunionDashboardTabs from "./modules/reunion/ReunionDashboardTabs";

import ReunionCreateEvent from "./modules/reunion/ReunionCreateEvent";
import ReunionEventControl from "./modules/reunion/ReunionEventControl";
import ReunionLiveFeed from "./modules/reunion/ReunionLiveFeed";
import ReunionWinners from "./modules/reunion/ReunionWinners";
import ReunionDashboardFooter from "./modules/reunion/ReunionDashboardFooter";

import useReunionAdmin from "./modules/reunion/hooks/useReunionAdmin";

const ReunionOrganisorDashboard = () => {
  const [activeTab, setActiveTab] = useState("display");
  const admin = useReunionAdmin();

  useEffect(() => {
    admin.loadMyEvent();
    // eslint-disable-next-line
  }, []);

  const { event, loading, error } = admin;

  // First-run state: organiser has no event yet at all. ReunionCreateEvent
  // handles both "create new" and "edit existing" — see that file.
  if (loading && !event) {
    return (
      <div className="min-h-screen bg-[#07110f] text-white flex items-center justify-center">
        <div className="text-emerald-300 text-lg animate-pulse">
          Loading your Reunion...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07110f] text-white">
      {event ? (
        <ReunionDashboardHero event={event} />
      ) : (
        <div className="max-w-7xl mx-auto px-4 pt-10 pb-4">
          <h1 className="text-3xl font-black">👋 Welcome — let's set up your first Reunion</h1>
          <p className="text-emerald-200 mt-2">
            Fill in the details below to create your event.
          </p>
        </div>
      )}

      <ReunionDashboardTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 px-4 py-3">
            ⚠ {error}
          </div>
        )}

        {activeTab === "display" && (
          <ReunionCreateEvent
            event={event}
            onCreate={admin.createEvent}
            onUpdate={admin.updateEvent}
          />
        )}

        {activeTab === "start" && event && (
          <ReunionEventControl
            mode="start"
            event={event}
            onStart={admin.startEvent}
            onPause={admin.pauseEvent}
            onResume={admin.resumeEvent}
          />
        )}

        {activeTab === "live" && event && (
          <ReunionLiveFeed
            event={event}
            liveFeed={admin.liveFeed}
            startPolling={admin.startLiveFeedPolling}
            stopPolling={admin.stopLiveFeedPolling}
          />
        )}

        {activeTab === "stop" && event && (
          <ReunionEventControl
            mode="stop"
            event={event}
            onStop={admin.stopEvent}
            onPause={admin.pauseEvent}
            onResume={admin.resumeEvent}
          />
        )}

        {activeTab === "winner" && event && (
          <ReunionWinners
            event={event}
            fetchWinners={admin.fetchWinners}
            finalizeResults={admin.finalizeResults}
          />
        )}

        {["start", "live", "stop", "winner"].includes(activeTab) && !event && (
          <div className="rounded-2xl bg-yellow-500/10 border border-yellow-500/30 p-8 text-center text-yellow-200">
            Set up your Reunion under "Display Settings" first.
          </div>
        )}
      </div>

      <ReunionDashboardFooter />
    </div>
  );
};

export default ReunionOrganisorDashboard;
